import React, { useMemo } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Fallback from '@/components/uiKit/Fallback';
import { ErrorBoundary } from 'react-error-boundary';
import { SettingsSidePane } from '@/components/pages/SettingsPage/SidePane/SettingsSidePane';
import { Layout } from '@/layouts';
import { Preferences } from '@/components/pages/SettingsPage/MainContent/Preferences/Preferences';
import { AccountSettings } from '@/components/pages/SettingsPage/MainContent/AccountSettings/AccountSettings';
import { UserManagement } from '@/components/pages/SettingsPage/MainContent/UserManagement/UserManagement';
import { NotificationPreferences } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/NotificationPreferences';
import { UserPreferences } from '@/components/pages/SettingsPage/MainContent/UserManagement/UserPreferences';
import { settingsLocations } from '@/components/pages/SettingsPage/MainContent/locations';
import { CreateUser } from '@/components/pages/SettingsPage/MainContent/UserManagement/CreateUser';
import { MANAGEMENT_ROLE } from '@/const';
import { useUserData } from '@/components/pages/SettingsPage/MainContent/shared/useUserData';
import { RefreshCwIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsDMAdmin } from '@/hooks';
import useSitesPage from '@/hooks/SitesPage/useSitesPage';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: MANAGEMENT_ROLE[];
  userRole?: string;
  redirectPath?: string;
}

const ProtectedRoute = ({
  children,
  allowedRoles,
  userRole,
  redirectPath = '/settings/account',
}: ProtectedRouteProps) => {
  if (!userRole || !allowedRoles.includes(userRole as MANAGEMENT_ROLE)) {
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

export const SettingsPage = () => {
  const { t } = useTranslation();
  const { userData } = useUserData();
  const { isDMAdmin } = useIsDMAdmin();
  const { isLoading, data, getSitesData } = useSitesPage();

  const routesConfig = useMemo(() => {
    if (userData?.role) {
      return [
        {
          index: true,
          element: <Navigate to="account" />,
        },
        {
          path: settingsLocations.accountSettings,
          element: <AccountSettings />,
        },
        {
          path: settingsLocations.userManagement,
          element: (
            <ProtectedRoute allowedRoles={[MANAGEMENT_ROLE.DM_Admin, MANAGEMENT_ROLE.Admin]} userRole={userData?.role}>
              <UserManagement />
            </ProtectedRoute>
          ),
        },
        {
          path: settingsLocations.userCreate,
          element: (
            <ProtectedRoute allowedRoles={[MANAGEMENT_ROLE.DM_Admin, MANAGEMENT_ROLE.Admin]} userRole={userData?.role}>
              <CreateUser />
            </ProtectedRoute>
          ),
        },
        {
          path: settingsLocations.userEdit,
          element: (
            <ProtectedRoute allowedRoles={[MANAGEMENT_ROLE.DM_Admin, MANAGEMENT_ROLE.Admin]} userRole={userData?.role}>
              <UserPreferences />
            </ProtectedRoute>
          ),
        },
        {
          path: settingsLocations.notificationPreferences,
          element: (
            <ProtectedRoute allowedRoles={[MANAGEMENT_ROLE.DM_Admin, MANAGEMENT_ROLE.Admin]} userRole={userData?.role}>
              <NotificationPreferences />
            </ProtectedRoute>
          ),
        },
        {
          path: settingsLocations.applicationPreferences,
          element: (
            <ProtectedRoute allowedRoles={[MANAGEMENT_ROLE.DM_Admin]} userRole={userData?.role}>
              <Preferences />
            </ProtectedRoute>
          ),
        },
        {
          path: '*',
          element: <Navigate to="/settings/account" replace />,
        },
      ];
    } else {
      return [];
    }
  }, [userData?.role]);

  const routes = useRoutes(routesConfig);

  return (
    <Layout>
      <div className="flex flex-col md:px-8 w-full h-full gap-4 px-2 py-0 pt-6 max-h-screen">
        <div className="h-full flex gap-4 relative">
          <div className="flex flex-col gap-4 w-1/5 h-full overflow-hidden min-w-[250px]">
            {userData ? <SettingsSidePane /> : null}
          </div>

          <div className="flex h-full flex-col gap-2 relative w-4/5">
            <ErrorBoundary FallbackComponent={Fallback}>{routes}</ErrorBoundary>
          </div>
        </div>
        {isDMAdmin && (
          <Button
            className="absolute bottom-6 right-6"
            variant={'filled'}
            disabled={false}
            onClick={() => {
              getSitesData(true);
            }}
          >
            <RefreshCwIcon className={`${isLoading ? 'animate-spin' : ''}`} />{' '}
{`${isLoading ? t('general.loading') : t('general.refreshCache')}`}
          </Button>
        )}
      </div>
    </Layout>
  );
};
